import { promises as fs } from "fs";
import type { Tweet, TweetReply } from "@podx/core/types";
import { CryptoAnalyzer } from "../crypto/index.js";
import { BaseScraper } from "./baseScraper";

export class CommentScraper extends BaseScraper {
  private cryptoAnalyzer: any;

  constructor() {
    super();
    this.cryptoAnalyzer = new CryptoAnalyzer();
  }

  // Extract user location data from profile
  async extractUserLocation(username: string): Promise<{
    userLocation?: string;
    profileLocation?: string;
    geoData?: {
      coordinates?: [number, number];
      place?: string;
      country?: string;
      region?: string;
    };
  }> {
    try {
      // Location fetching not implemented; return empty object for now
      return {};
    } catch (error) {
      console.warn(`⚠️  Could not fetch location for @${username}:`, error);
      return {};
    }
  }

  // Parse location string to extract geographical data
  private parseLocationString(location?: string): {
    coordinates?: [number, number];
    place?: string;
    country?: string;
    region?: string;
  } | undefined {
    if (!location) return undefined;

    const locationLower = location.toLowerCase();
    
    // Extract country information
    const countries = {
      'usa': 'United States', 'us': 'United States', 'america': 'United States',
      'uk': 'United Kingdom', 'britain': 'United Kingdom', 'england': 'United Kingdom',
      'canada': 'Canada', 'ca': 'Canada',
      'australia': 'Australia', 'au': 'Australia',
      'germany': 'Germany', 'de': 'Germany',
      'france': 'France', 'fr': 'France',
      'japan': 'Japan', 'jp': 'Japan',
      'singapore': 'Singapore', 'sg': 'Singapore',
      'india': 'India', 'in': 'India',
      'brazil': 'Brazil', 'br': 'Brazil',
      'china': 'China', 'cn': 'China'
    };

    let detectedCountry: string | undefined;
    for (const [key, value] of Object.entries(countries)) {
      if (locationLower.includes(key)) {
        detectedCountry = value;
        break;
      }
    }

    // Extract major cities and their approximate coordinates
    const cities: Record<string, { coords: [number, number]; country: string; region: string }> = {
      'new york': { coords: [-74.006, 40.7128], country: 'United States', region: 'NY' },
      'london': { coords: [-0.1276, 51.5074], country: 'United Kingdom', region: 'England' },
      'tokyo': { coords: [139.6917, 35.6895], country: 'Japan', region: 'Tokyo' },
      'singapore': { coords: [103.8198, 1.3521], country: 'Singapore', region: 'Singapore' },
      'san francisco': { coords: [-122.4194, 37.7749], country: 'United States', region: 'CA' },
      'los angeles': { coords: [-118.2437, 34.0522], country: 'United States', region: 'CA' },
      'miami': { coords: [-80.1918, 25.7617], country: 'United States', region: 'FL' },
      'berlin': { coords: [13.4050, 52.5200], country: 'Germany', region: 'Berlin' },
      'paris': { coords: [2.3522, 48.8566], country: 'France', region: 'Île-de-France' },
      'sydney': { coords: [151.2093, -33.8688], country: 'Australia', region: 'NSW' }
    };

    let cityData: { coords: [number, number]; country: string; region: string } | undefined;
    for (const [city, data] of Object.entries(cities)) {
      if (locationLower.includes(city)) {
        cityData = data;
        break;
      }
    }

    const result: { coordinates?: [number, number]; place?: string; country?: string; region?: string } = {};
    result.place = location;
    const c = cityData?.country || detectedCountry;
    if (c !== undefined) result.country = c;
    if (cityData?.region !== undefined) result.region = cityData.region;
    if (cityData?.coords) result.coordinates = cityData.coords as [number, number];
    return result;
  }

  async scrapeTweetReplies(tweetId: string, maxReplies: number = 50): Promise<TweetReply[]> {
    try {
      console.log(`🔍 Scraping replies for tweet ${tweetId}...`);

      const replies: TweetReply[] = [];
      await this.initializeScraper();
      this.ensureInitialized();
      const replyGenerator: any = (this.scraper as any).getTweetsAndReplies
        ? (this.scraper as any).getTweetsAndReplies(tweetId, maxReplies)
        : [];

      for await (const reply of replyGenerator) {
        const processedReply = await this.processReply(reply, tweetId);
        if (processedReply) {
          replies.push(processedReply);
        }

        if (replies.length >= maxReplies) break;
      }

      console.log(`✅ Scraped ${replies.length} replies for tweet ${tweetId}`);
      return replies;

    } catch (error) {
      console.error(`❌ Failed to scrape replies for tweet ${tweetId}:`, error);
      return [];
    }
  }

  private async processReply(rawReply: any, parentTweetId: string): Promise<TweetReply | null> {
    try {
      const reply: TweetReply = {
        id: rawReply.id || rawReply.conversationId,
        text: rawReply.text || rawReply.full_text || '',
        createdAt: rawReply.createdAt || rawReply.created_at || new Date().toISOString(),
        username: rawReply.username || 'unknown',
        name: rawReply.name || 'Unknown',
        likeCount: rawReply.likes || rawReply.likeCount || rawReply.favorite_count || 0,
        retweetCount: rawReply.retweets || rawReply.retweetCount || rawReply.retweet_count || 0,
        parentTweetId
      };

      // Analyze sentiment of the reply
      const sentimentScore = this.cryptoAnalyzer.analyzeSentimentScore([{
        id: reply.id,
        text: reply.text,
        createdAt: reply.createdAt,
        retweetCount: reply.retweetCount,
        likeCount: reply.likeCount
      } as Tweet]);

      reply.sentimentScore = sentimentScore;
      reply.sentiment = this.cryptoAnalyzer.scoreToSentiment(sentimentScore);

      // Extract location data for the reply author
      if (reply.username && reply.username !== 'unknown') {
        try {
          const locationData = await this.extractUserLocation(reply.username);
          if (locationData.userLocation !== undefined) reply.userLocation = locationData.userLocation;
          if (locationData.profileLocation !== undefined) reply.profileLocation = locationData.profileLocation;
          if (locationData.geoData !== undefined) reply.geoData = locationData.geoData;
        } catch (error) {
          // Location extraction failed, but continue with the reply
          console.warn(`Could not extract location for @${reply.username}`);
        }
      }

      return reply;

    } catch (error) {
      console.error('❌ Failed to process reply:', error);
      return null;
    }
  }

  async scrapeMultipleTweetsReplies(tweetIds: string[], maxRepliesPerTweet: number = 20): Promise<Map<string, TweetReply[]>> {
    const results = new Map<string, TweetReply[]>();

    console.log(`🚀 Starting to scrape replies for ${tweetIds.length} tweets...`);

    for (let i = 0; i < tweetIds.length; i++) {
      const tweetId = tweetIds[i];
      if (!tweetId) continue;
      console.log(`📊 Progress: ${i + 1}/${tweetIds.length} tweets processed`);

      const replies = await this.scrapeTweetReplies(tweetId, maxRepliesPerTweet);
      results.set(tweetId, replies);

      // Add small delay to avoid rate limiting
      if (i < tweetIds.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    console.log(`✅ Completed scraping replies for ${tweetIds.length} tweets`);
    return results;
  }

  async analyzeRepliesSentiment(replies: TweetReply[]): Promise<{
    overallSentiment: 'positive' | 'negative' | 'neutral';
    sentimentScore: number;
    positiveCount: number;
    negativeCount: number;
    neutralCount: number;
    topPositiveReplies: TweetReply[];
    topNegativeReplies: TweetReply[];
  }> {
    const positiveReplies = replies.filter(r => r.sentiment === 'positive');
    const negativeReplies = replies.filter(r => r.sentiment === 'negative');
    const neutralReplies = replies.filter(r => r.sentiment === 'neutral');

    const totalSentimentScore = replies.reduce((sum, r) => sum + (r.sentimentScore || 0), 0);
    const avgSentimentScore = replies.length > 0 ? totalSentimentScore / replies.length : 0;

    const overallSentiment = this.cryptoAnalyzer.scoreToSentiment(avgSentimentScore);

    return {
      overallSentiment,
      sentimentScore: avgSentimentScore,
      positiveCount: positiveReplies.length,
      negativeCount: negativeReplies.length,
      neutralCount: neutralReplies.length,
      topPositiveReplies: positiveReplies
        .sort((a, b) => (b.sentimentScore || 0) - (a.sentimentScore || 0))
        .slice(0, 5),
      topNegativeReplies: negativeReplies
        .sort((a, b) => (a.sentimentScore || 0) - (b.sentimentScore || 0))
        .slice(0, 5)
    };
  }

  async exportRepliesToFile(repliesMap: Map<string, TweetReply[]>, filename: string): Promise<void> {
    // Process sentiment analysis for each tweet's replies
    const tweetsData = [];
    const allReplies = Array.from(repliesMap.values()).flat();
    
    for (const [tweetId, replies] of repliesMap.entries()) {
      const sentimentAnalysis = await this.analyzeRepliesSentiment(replies);
      const locationAnalysis = this.analyzeLocationDistribution(replies);
      
      tweetsData.push({
        tweetId,
        replyCount: replies.length,
        replies: replies,
        sentimentAnalysis,
        locationAnalysis
      });
    }

    // Overall location analysis
    const overallLocationAnalysis = this.analyzeLocationDistribution(allReplies);

    const exportData = {
      generatedAt: new Date().toISOString(),
      totalTweets: repliesMap.size,
      totalReplies: allReplies.length,
      overallLocationAnalysis,
      tweets: tweetsData
    };

    await fs.writeFile(filename, JSON.stringify(exportData, null, 2));
    console.log(`💾 Replies exported to ${filename}`);
  }

  // Analyze location distribution of replies
  analyzeLocationDistribution(replies: TweetReply[]): {
    totalWithLocation: number;
    countries: { [key: string]: number };
    cities: { [key: string]: number };
    regions: { [key: string]: number };
    topCountries: Array<{ country: string; count: number; percentage: number }>;
    topCities: Array<{ city: string; count: number; percentage: number }>;
  } {
    const repliesWithLocation = replies.filter(r => r.geoData?.country || r.userLocation);
    const countries: { [key: string]: number } = {};
    const cities: { [key: string]: number } = {};
    const regions: { [key: string]: number } = {};

    repliesWithLocation.forEach(reply => {
      if (reply.geoData?.country) {
        countries[reply.geoData.country] = (countries[reply.geoData.country] || 0) + 1;
      }
      if (reply.geoData?.place) {
        cities[reply.geoData.place] = (cities[reply.geoData.place] || 0) + 1;
      }
      if (reply.geoData?.region) {
        regions[reply.geoData.region] = (regions[reply.geoData.region] || 0) + 1;
      }
    });

    const topCountries = Object.entries(countries)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10)
      .map(([country, count]) => ({
        country,
        count,
        percentage: Math.round((count / repliesWithLocation.length) * 100)
      }));

    const topCities = Object.entries(cities)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10)
      .map(([city, count]) => ({
        city,
        count,
        percentage: Math.round((count / repliesWithLocation.length) * 100)
      }));

    return {
      totalWithLocation: repliesWithLocation.length,
      countries,
      cities,
      regions,
      topCountries,
      topCities
    };
  }

  // Helper method to extract tweet IDs that mention specific tokens
  extractTokenTweetIds(token: string, tweets: Tweet[]): string[] {
    return tweets
      .filter(tweet => {
        const text = tweet.text.toLowerCase();
        const tokenLower = token.toLowerCase();
        return text.includes(`$${tokenLower}`) && !this.isMonetaryAmount(`$${token}`);
      })
      .map(tweet => tweet.id)
      .slice(0, 10); // Limit to avoid rate limiting
  }

  private isMonetaryAmount(token: string): boolean {
    // Check against known monetary patterns
    const monetaryPatterns = [
      /^\$\d+[KMGTPEZY]?/,  // $50, $10MM, $750K, etc.
      /^\$\d+\.\d+/,        // $50.00, etc.
      /^\$\d+[MB]illion/,   // $1Million, $2Billion, etc.
      /^\$\d+[KMB]/,        // $5K, $10M, $1B, etc.
    ];

    for (const pattern of monetaryPatterns) {
      if (pattern.test(token)) {
        return true;
      }
    }

    const tokenWithoutDollar = token.substring(1);

    // If it starts with a number, it's likely a monetary amount
    if (/^\d/.test(tokenWithoutDollar)) {
      return true;
    }

    // If it contains only numbers and common monetary suffixes, exclude it
    if (/^\d+[KMBT]?/.test(tokenWithoutDollar)) {
      return true;
    }

    return false;
  }
}
